import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { userRequest, publicRequest } from "../../requestMethods";
import { FiSave, FiX, FiSearch, FiUsers } from "react-icons/fi";
import "./newJournal.css";

const NewJournal = () => {
    const history = useHistory();
    const currentUser = useSelector((state) => state.user.currentUser);
    const installerManager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "installerManager"
            : null
    );
    const productionManager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "productionManager"
            : null
    );
    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    // Check if user can create journals (installerManager OR manager OR productionManager NOT with principle 63429be7a4b0715d61b8955c OR productionManager with JINSORA)
    const canCreateJournal = installerManager || manager || 
                            (productionManager && currentUser?.principle !== "63429be7a4b0715d61b8955c") ||
                            (productionManager && process.env.REACT_APP_PROJECT === "JINSORA");

    const [orderSearch, setOrderSearch] = useState("");
    const [orderOptions, setOrderOptions] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [installersOptions, setInstallersOptions] = useState([]);
    const [selectedInstallers, setSelectedInstallers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [installationDate, setInstallationDate] = useState("");
    const [installationTime, setInstallationTime] = useState("");
    const [journalType, setJournalType] = useState("installation");

    // Fetch installers on component mount
    useEffect(() => {
        const fetchInstallers = async () => {
            try {
                // Only show installers from the same principle
                const params = {
                    type: "installer",
                    isAdmin: true,
                };

                const res = await publicRequest.get("/users/admin", {
                    params,
                });
                
                const userPrinciple = currentUser?.principle?._id || currentUser?.principle;
                const filteredInstallers = res.data.filter((installer) => {
                    const installerPrinciple = installer.principle?._id || installer.principle;
                    return !userPrinciple || installerPrinciple === userPrinciple;
                });

                const formattedInstallers = filteredInstallers.map((installer) => ({
                    label: installer.name,
                    value: installer._id,
                }));
                
                setInstallersOptions(formattedInstallers);
            } catch (error) {
                console.error("Error fetching installers:", error);
                alert("Failed to fetch installers");
            }
        };

        fetchInstallers();
    }, [currentUser]);

    // Search orders with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (orderSearch && orderSearch.length >= 2) {
                searchOrders(orderSearch);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [orderSearch]);

    const searchOrders = async (searchTerm) => {
        setIsSearching(true);
        try {
            const userPrinciple = currentUser?.principle?._id || currentUser?.principle;
            const filters = {
                coNumber: searchTerm,
                limit: 20,
                page: 1,
                ...(userPrinciple && { principle: userPrinciple }),
            };

            const res = await publicRequest.get("/orders", {
                params: { filters: JSON.stringify(filters) },
            });

            const formattedOrders = res.data.payload.map((order) => ({
                label: `${order.invoiceId} - ${order.coNumber}`,
                value: order._id,
                order: order,
            }));

            setOrderOptions(formattedOrders);
        } catch (error) {
            console.error("Error searching orders:", error);
            alert("Failed to search orders");
        } finally {
            setIsSearching(false);
        }
    };

    const handleCreateJournal = async () => {
        // Validation
        if (!selectedOrder) {
            alert("Please select an order");
            return;
        }

        if (selectedInstallers.length === 0) {
            alert("Please select at least one installer");
            return;
        }

        setIsLoading(true);
        try {
            // Combine date and time into a single Date object
            let createdAt = new Date();
            if (installationDate) {
                if (installationTime) {
                    // Combine date and time
                    const dateTimeString = `${installationDate}T${installationTime}`;
                    createdAt = new Date(dateTimeString);
                } else {
                    // Use date only, set time to current time
                    const dateOnly = new Date(installationDate);
                    const now = new Date();
                    dateOnly.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
                    createdAt = dateOnly;
                }
            }

            const journalData = {
                order: selectedOrder.value,
                installers: selectedInstallers.map((inst) => inst.value),
                createdAt: createdAt.toISOString(),
                type: journalType,
            };

            const res = await userRequest.post("/journal", journalData);

            if (res.data) {
                alert("Journal created successfully!");
            }
        } catch (error) {
            console.error("Error creating journal:", error);
            alert(error.response?.data || "Failed to create journal. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    // Check if user can create journals
    if (!canCreateJournal && !currentUser?.isSuperAdmin) {
        return (
            <div className="newJournal">
                <div className="newJournalContainer">
                    <div className="unauthorized">
                        <h2>Access Denied</h2>
                        <p>You do not have permission to create journals.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="newJournal">
            <div className="newJournalContainer">
                <div className="newJournalHeader">
                    <h1 className="newJournalTitle">Create New Journal</h1>
                    <p className="newJournalSubtitle">Create a journal entry for order installation</p>
                </div>

                <div className="newJournalForm">
                    {/* Order Search and Selection */}
                    <div className="formGroup">
                        <label className="formLabel">
                            <FiSearch className="labelIcon" />
                            Search Order *
                        </label>
                        <Select
                            options={orderOptions}
                            value={selectedOrder}
                            onInputChange={(value) => setOrderSearch(value)}
                            onChange={(selected) => setSelectedOrder(selected)}
                            placeholder="Type co number to search..."
                            isLoading={isSearching}
                            isClearable
                            {...commonSelectProps}
                        />
                        <span className="formHint">
                            Type at least 2 characters to search for orders
                        </span>
                        
                        {selectedOrder && selectedOrder.order && (
                            <div className="orderDetails">
                                <h4>Order Details</h4>
                                <div className="orderDetailsGrid">
                                    <div className="orderDetailItem">
                                        <span className="detailLabel">Invoice:</span>
                                        <span className="detailValue">{selectedOrder.order.invoiceId}</span>
                                    </div>
                                    <div className="orderDetailItem">
                                        <span className="detailLabel">CO Number:</span>
                                        <span className="detailValue">{selectedOrder.order.coNumber}</span>
                                    </div>
                                    <div className="orderDetailItem">
                                        <span className="detailLabel">Customer:</span>
                                        <span className="detailValue">{selectedOrder.order.userId?.name}</span>
                                    </div>
                                    <div className="orderDetailItem">
                                        <span className="detailLabel">Status:</span>
                                        <span className="detailValue capitalize">{selectedOrder.order.status}</span>
                                    </div>
                                    <div className="orderDetailItem">
                                        <span className="detailLabel">Amount:</span>
                                        <span className="detailValue">RM {selectedOrder.order.amount}</span>
                                    </div>
                                    <div className="orderDetailItem" style={{ gridColumn: "1 / -1" }}>
                                        <span className="detailLabel">Address:</span>
                                        <span className="detailValue">{selectedOrder.order.address || "Not provided"}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Installers Selection */}
                    <div className="formGroup">
                        <label className="formLabel">
                            <FiUsers className="labelIcon" />
                            Select Installers *
                        </label>
                        <Select
                            options={installersOptions}
                            value={selectedInstallers}
                            onChange={(selected) => setSelectedInstallers(selected || [])}
                            placeholder="Select one or more installers..."
                            isMulti
                            {...commonSelectProps}
                        />
                        <span className="formHint">
                            Select all installers assigned to this job
                        </span>
                    </div>

                    {/* Installation Date and Time */}
                    <div className="formGroup">
                        <label className="formLabel">
                            Installation Date & Time
                        </label>
                        <div className="installationDatetimeGrid">
                            <div>
                                <input
                                    type="date"
                                    value={installationDate}
                                    onChange={(e) => setInstallationDate(e.target.value)}
                                    className="formInput"
                                    style={{ width: "100%" }}
                                />
                                <span className="formHint" style={{ display: "block", marginTop: "0.5rem" }}>
                                    Select installation date
                                </span>
                            </div>
                            <div>
                                <input
                                    type="time"
                                    value={installationTime}
                                    onChange={(e) => setInstallationTime(e.target.value)}
                                    className="formInput timeInput"
                                    style={{ width: "100%" }}
                                />
                                <span className="formHint" style={{ display: "block", marginTop: "0.5rem" }}>
                                    Select installation time
                                </span>
                            </div>
                        </div>
                        <span className="formHint" style={{ display: "block", marginTop: "0.5rem" }}>
                            Leave empty to use current date and time
                        </span>
                    </div>

                    {/* Type Selection */}
                    <div className="formGroup">
                        <label className="formLabel">
                            Type *
                        </label>
                        <div style={{ display: "flex", gap: "1.5rem" }}>
                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="journalType"
                                    value="installation"
                                    checked={journalType === "installation"}
                                    onChange={(e) => setJournalType(e.target.value)}
                                    style={{ width: "16px", height: "16px", marginRight: "8px" }}
                                />
                                <span>Installation</span>
                            </label>
                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="journalType"
                                    value="service"
                                    checked={journalType === "service"}
                                    onChange={(e) => setJournalType(e.target.value)}
                                    style={{ width: "16px", height: "16px", marginRight: "8px" }}
                                />
                                <span>Service</span>
                            </label>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="formActions">
                        <button
                            className="btnPrimary"
                            onClick={handleCreateJournal}
                            disabled={isLoading}
                        >
                            <FiSave className="btnIcon" />
                            {isLoading ? "Creating..." : "Create Journal"}
                        </button>
                        <button
                            className="btnSecondary"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            <FiX className="btnIcon" />
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewJournal;

